import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, Store, Bike, CalendarDays, Printer, Loader2, XCircle } from "lucide-react";
import { api, rm, STATUS } from "@/lib/api";
import { PhoneFrame } from "@/components/customer/CustomerShell";

export default function OrderSuccess() {
  const { id } = useParams();
  const nav = useNavigate();
  const [o, setO] = useState(null);

  useEffect(() => {
    const load = () => api.get(`/orders/${id}`).then((r) => {
      if (r.data.payment_status !== "menunggu") return setO(r.data);
      return api.post(`/orders/${id}/check-payment`).then((c) => setO(c.data)).catch(() => setO(r.data));
    }).catch(() => setO(false));
    load();
    const t = setInterval(load, 8000);
    return () => clearInterval(t);
  }, [id]);

  if (o === false) return <PhoneFrame><p className="p-6 text-sm">Pesanan tidak dijumpai.</p></PhoneFrame>;
  if (!o) return <PhoneFrame><p className="p-6 text-sm text-slate-500">Memuatkan...</p></PhoneFrame>;
  const st = STATUS[o.status];
  const waiting = o.payment_status === "menunggu";
  const failed = o.payment_status === "gagal";
  if (o.status === "batal") return (
    <PhoneFrame>
      <div className="bg-rose-600 text-white px-6 pt-14 pb-10 rounded-b-[28px] text-center rise" data-testid="order-cancelled-banner">
        <XCircle className="w-16 h-16 mx-auto" />
        <h1 className="font-display text-2xl font-extrabold mt-4">Pesanan Dibatalkan</h1>
        <p className="text-white/90 text-sm mt-1">Pesanan ini telah dibatalkan oleh restoran.</p>
        <p className="mt-4 inline-block bg-white/15 rounded-full px-4 py-1 font-bold tracking-wide">#{o.order_no} · {rm(o.total)}</p>
      </div>
      <div className="px-5 py-6"><button onClick={() => nav("/")} data-testid="cancelled-home-button" className="w-full h-12 rounded-xl border border-[#B91C1C] text-[#B91C1C] font-semibold">Kembali ke Menu</button></div>
    </PhoneFrame>
  );
  if (waiting || failed) return (
    <PhoneFrame>
      <div className={`${failed ? "bg-red-600" : "bg-amber-500"} text-white px-6 pt-14 pb-10 rounded-b-[28px] text-center rise`} data-testid="payment-pending-banner">
        {failed ? <XCircle className="w-16 h-16 mx-auto" /> : <Loader2 className="w-16 h-16 mx-auto animate-spin" />}
        <h1 className="font-display text-2xl font-extrabold mt-4" data-testid="payment-state-title">{failed ? "Bayaran Tidak Berjaya" : "Menunggu Pengesahan Bayaran"}</h1>
        <p className="text-white/90 text-sm mt-1">{failed ? "Pesanan tidak dihantar ke restoran. Sila buat pesanan semula." : "Pesanan akan dihantar ke restoran sebaik bayaran disahkan."}</p>
        <p className="mt-4 inline-block bg-white/15 rounded-full px-4 py-1 font-bold tracking-wide">#{o.order_no} · {rm(o.total)}</p>
      </div>
      <div className="px-5 py-6 space-y-3">
        {waiting && o.payment_url && <a href={o.payment_url} data-testid="payment-continue-link" className="w-full h-12 rounded-xl bg-[#B91C1C] text-white font-semibold flex items-center justify-center">Teruskan Bayaran</a>}
        <button onClick={() => nav("/")} data-testid="success-home-button" className="w-full h-12 rounded-xl border border-[#B91C1C] text-[#B91C1C] font-semibold">Kembali ke Menu</button>
      </div>
    </PhoneFrame>
  );

  return (
    <PhoneFrame>
      <div className="bg-[#B91C1C] text-white px-6 pt-14 pb-10 rounded-b-[28px] text-center rise">
        <CheckCircle2 className="w-16 h-16 mx-auto" />
        <h1 className="font-display text-2xl font-extrabold mt-4">Pesanan Diterima!</h1>
        <p className="text-white/85 text-sm mt-1">Pesanan anda telah dihantar ke restoran melalui WhatsApp.</p>
        <p className="mt-4 inline-block bg-white/15 rounded-full px-4 py-1 font-bold tracking-wide" data-testid="success-order-no">#{o.order_no}</p>
      </div>
      <div className="px-5 py-6 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-slate-600">Status</span>
          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${st.cls}`} data-testid="success-status">{st.label}</span>
        </div>
        <div className="bg-white rounded-2xl border p-4 space-y-2 text-sm">
          <p className="flex items-center gap-2">{o.mode === "pickup" ? <Store className="w-4 h-4" /> : <Bike className="w-4 h-4" />}{o.mode === "pickup" ? "Ambil di Restoran" : `Penghantaran: ${o.address}`}</p>
          <p className="flex items-center gap-2"><CalendarDays className="w-4 h-4" />{o.pickup_time}</p>
        </div>
        <div className="bg-white rounded-2xl border p-4" data-testid="customer-receipt">
          <div className="text-center border-b border-dashed pb-3 mb-2">
            <p className="font-display font-extrabold">POKYA RESTAURANT</p>
            <p className="text-xs text-slate-500">Resit Pesanan #{o.order_no}</p>
            <p className="text-xs text-slate-500" data-testid="receipt-date">{new Date(o.created_at).toLocaleString("ms-MY", { timeZone: "Asia/Kuala_Lumpur", dateStyle: "medium", timeStyle: "short" })}</p>
            <p className="text-xs text-slate-500">{o.customer_name} · {o.phone}</p>
            {o.loyverse_receipt && <p className="text-xs text-slate-500" data-testid="receipt-loyverse-no">No. Resit POS: {o.loyverse_receipt}</p>}
          </div>
          {o.items.map((it, i) => (
            <div key={i} className="flex justify-between text-sm py-1.5">
              <span>{it.name}{it.option ? ` (${it.option})` : ""} ×{it.qty}<span className="block text-xs text-slate-500">{rm(it.unit_price)} seunit{it.note ? ` · ${it.note}` : ""}</span></span>
              <span className="font-semibold">{rm(it.unit_price * it.qty)}</span>
            </div>
          ))}
          <div className="flex justify-between border-t border-dashed mt-2 pt-3 font-bold">
            <span>Jumlah</span><span data-testid="success-total">{rm(o.total)}</span>
          </div>
          <p className="text-center text-xs font-semibold mt-2" data-testid="receipt-payment-status">{o.payment_status === "dibayar" ? "SUDAH DIBAYAR ONLINE" : o.payment_status === "qr" ? "BAYAR TERUS (QR) · bukti dihantar" : "Bayar di kedai"}</p>
          <p className="text-center text-xs text-slate-500 mt-3">Terima kasih kerana memesan!</p>
        </div>
        <button onClick={() => window.print()} data-testid="receipt-print-button" className="w-full h-12 rounded-xl border border-slate-300 font-semibold flex items-center justify-center gap-2 print:hidden"><Printer className="w-4 h-4" />Cetak / Simpan Resit</button>
        <button onClick={() => nav("/")} data-testid="success-home-button" className="w-full h-12 rounded-xl bg-[#B91C1C] text-white font-semibold active:scale-95 transition-transform print:hidden">Kembali ke Menu</button>
        <button onClick={() => nav("/akaun")} data-testid="success-history-button" className="w-full h-12 rounded-xl border border-[#B91C1C] text-[#B91C1C] font-semibold print:hidden">Lihat Sejarah Pesanan</button>
      </div>
    </PhoneFrame>
  );
}
